import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Spinner } from '@/components/ui/spinner';
import { TabButton } from '@/components/ui/tab-button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Copy,
  Check,
  Pause,
  Play,
  ExternalLink,
  Search,
  AlertTriangle,
  Trash2,
  UserPlus,
  Mail,
} from 'lucide-react';
import {
  adminApi,
  AdminOrganization,
  AdminUser,
  AdminInvitation,
  OrganizationSubscription,
  UpdateOrganizationRequest,
  ProductEntitlement,
  OrganizationUsageSummary,
  OrgFeedbackItem,
} from '../services/adminApi';
import { useAuth } from '../auth/ParsleeAuthProvider';
import OrgBillingTab from './OrgBillingTab';
import { OnboardingUsageCard } from './OnboardingUsageCard';
import { TokenUsageTab } from './tokenUsage/TokenUsageTab';
import { PRODUCT_CATALOG } from './productCatalog';
import { resolveUsageSummaryFailure } from './usageSummaryState';
import {
  getOrgDetailCardCollectionReturnPath,
  resolveCardCollectionReturn,
  resolveOrgDetailBackDestination,
  resolveOrgDetailInitialTab,
} from './adminNavigation';
import { AddStaffMemberDialog } from './AddStaffMemberDialog';
import { ClientLimitDialog } from './ClientLimitDialog';
import {
  canOpenLimit,
  createUsersReader,
  isCurrentLimitOp,
  limitRowsAfterRead,
  limitRowsMessage,
  limitTargetUser,
  openLimitTarget,
  replaceUser,
  summarizeScope,
  type ClientLimitTarget,
  type LimitRowsState,
  type UsersReadResult,
} from './clientLimits';
import { InviteCustomerDialog } from './InviteCustomerDialog';
import { InvitationsPanel } from './InvitationsPanel';
import { OrgProvisioningBadges } from './OrgProvisioningBadges';
import {
  createInvitationsReader,
  invitationsForOrg,
  normalizeInvitationRole,
  pageInvitation,
  resolveInvitationsAvailability,
  usableAcceptUrl,
  type InvitationsAvailability,
  type InvitationsReadResult,
  type OrgInvitations,
} from './orgProvisioning';
import { useInvitationActions } from './useInvitationActions';
import './AdminDashboard.css';

type DetailTab = 'overview' | 'billing' | 'tokenUsage' | 'users' | 'feedback' | 'settings';

// Plain-language kind label for a feedback row. A commented thumbs-down arrives
// as feedbackKind null + type bug, and correctly reads "Bug report" (the
// plain-language equivalent of "thumbs down promoted to work").
function getFeedbackKindLabel(row: Pick<OrgFeedbackItem, 'feedbackKind' | 'type'>): string {
  if (row.feedbackKind === 'telemetry_positive') return 'Thumbs up';
  if (row.feedbackKind === 'telemetry_negative') return 'Thumbs down (signal)';
  if ((row.type || '').toLowerCase() === 'feature') return 'Feature request';
  return 'Bug report';
}

function truncateExcerpt(text: string | null, maxLength = 100): string {
  if (!text) return '-';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

// Raw triage status enum -> plain language, per the U11 label ruling (no internal
// tracker ids or jargon in user-facing surfaces).
const FEEDBACK_STATUS_LABELS: Record<string, string> = {
  New: 'New',
  Confirmed: 'Confirmed',
  Triaging: 'Being investigated',
  PrCreated: 'Fix in review',
  RevisionRequested: 'Fix being revised',
  Deployed: 'Fixed & shipped',
  NotRepro: 'Could not reproduce',
  NeedsHuman: 'Needs a human',
};

// Status color semantics preserved from the Fluent Badge mapping:
// success/emerald, warning/amber, destructive/red, neutral/gray.
const ON_EMERALD = 'border-transparent bg-gold/15 text-foreground';
const ON_AMBER = 'border-transparent bg-warning/10 text-warning';
const ON_RED = 'border-transparent bg-destructive/10 text-destructive';
const NEUTRAL_GRAY = 'border-transparent bg-muted text-text-secondary';
const OFF_GRAY = 'border-border text-muted-foreground';

function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span className="copyable-id" title={value}>
      {label && <span className="copyable-id-label">{label}: </span>}
      <code>{value.length > 20 ? value.slice(0, 8) + '...' + value.slice(-8) : value}</code>
      <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
        {copied ? <Check /> : <Copy />}
      </button>
    </span>
  );
}

// Inline, scoped error affordance for a single non-critical section (users / subscriptions /
// entitlements). Mirrors the duplicate-detection banner idiom below — a small inline strip near
// the affected card/tab, never the whole-page error banner.
function SectionError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="border border-destructive/30 bg-destructive/10" style={{
      padding: '0.5rem 0.75rem',
      borderRadius: '6px',
      marginTop: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    }}>
      <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
      <span>{message}</span>
    </div>
  );
}

const CONNECTION_TYPE_LABELS: Record<string, string> = {
  DelegatedAccess: 'Self-Service',
  AdminConsent: 'Admin-Managed',
  ManagedIdentity: 'Internal (ButtonUp)',
  ServicePrincipal: 'Legacy',
};

function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { isParsleeStaff } = useAuth();
  const orgDetailSearch = window.location.search;
  const backDestination = resolveOrgDetailBackDestination(orgDetailSearch);
  const cardCollectionReturnPath = orgId
    ? getOrgDetailCardCollectionReturnPath(orgId, orgDetailSearch)
    : undefined;
  const [org, setOrg] = useState<AdminOrganization | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [subscriptions, setSubscriptions] = useState<OrganizationSubscription[]>([]);
  const [entitlements, setEntitlements] = useState<ProductEntitlement[]>([]);
  const [usageSummary, setUsageSummary] = useState<OrganizationUsageSummary | null>(null);
  const [usageSummaryLoading, setUsageSummaryLoading] = useState(false);
  const [usageSummaryRefreshing, setUsageSummaryRefreshing] = useState(false);
  const [usageSummaryError, setUsageSummaryError] = useState<string | null>(null);
  const [usageSummaryHidden, setUsageSummaryHidden] = useState(false);
  const [usageSummaryLastRefreshed, setUsageSummaryLastRefreshed] = useState<Date | null>(null);
  const usageSummarySeqRef = useRef(0);
  const [entitlementLoading, setEntitlementLoading] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per-section (non-critical) load errors. A single one of these must NOT blank the whole page —
  // only the org-identity load (page-level `error`) can do that.
  const [usersError, setUsersError] = useState<string | null>(null);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [entitlementsError, setEntitlementsError] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>(resolveOrgDetailInitialTab(orgDetailSearch));
  // Result of the Stripe card-collection round-trip (U4). The backend returns the rep to
  // /admin/orgs/<id>?org=<id>&card=collected|cancelled; we parse it once, open the Billing tab,
  // and hand the result to OrgBillingTab for an inline confirmation.
  const [cardResult, setCardResult] = useState<'collected' | 'cancelled' | null>(null);

  // Users tab state
  const [userSearch, setUserSearch] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [userStatusFilter, setUserStatusFilter] = useState<string>('Active');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  // Admin-created organizations (spec §7). The invitations list doubles as the feature probe: a
  // 404 means the API predates it, so the panel and both Users-tab buttons stay hidden. The list
  // carries the org it was read for: until the org-change reset lands, a list for another org
  // renders and serves as empty (`invitationsForOrg`), so an org switch never paints old rows.
  const [invitationsState, setInvitationsState] = useState<OrgInvitations>({ orgId, list: [] });
  // The list as last published, for the controller's callbacks: it reads currentInvitation right
  // after an awaited reconcile resolves, before React has rendered the list that read applied, so
  // every write updates this ref and state together instead of syncing the ref on render.
  const invitationsRef = useRef<OrgInvitations>(invitationsState);
  const publishInvitations = useCallback((forOrgId: string | undefined, list: AdminInvitation[]) => {
    const next = { orgId: forOrgId, list };
    invitationsRef.current = next;
    setInvitationsState(next);
  }, []);
  const [invitationsAvailability, setInvitationsAvailability] = useState<InvitationsAvailability>('unknown');
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [limitTarget, setLimitTarget] = useState<ClientLimitTarget | null>(null);
  const [limitRows, setLimitRows] = useState<LimitRowsState>('fresh');
  const [showInvite, setShowInvite] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState<{ email: string; role: string } | null>(null);
  // The org the page shows right now. The reader and applyInvitationUpdate compare every answer
  // and every send DTO with it, so work started for a previous org never lands here.
  const currentOrgIdRef = useRef(orgId);
  currentOrgIdRef.current = orgId;
  // Bumped on every "Limit to" open and every organization change; a save or close from an older open is dropped.
  const limitOpRef = useRef(0);
  const usersPageRef = useRef(usersPage);
  usersPageRef.current = usersPage;
  // Every Users-list read (first load, pagination, the refresh after a limit change) is ordered here: only the
  // latest read for the org on screen applies. An applied read is fresh, so it also releases the limit lock; a
  // failed one locks it if a refresh was waiting on it.
  const [usersReader] = useState(() =>
    createUsersReader({
      list: (id, skip) => adminApi.getOrganizationUsers(id, undefined, skip, 50),
      currentOrgId: () => currentOrgIdRef.current,
      onLoaded: (data) => {
        setUsers(data.users);
        setUserCount(data.totalCount);
        setUsersError(null);
        setLimitRows((rows) => limitRowsAfterRead(rows, 'applied'));
      },
      onFailed: (err) => {
        console.error('Failed to fetch users:', err);
        setLimitRows((rows) => limitRowsAfterRead(rows, 'failed'));
      },
    })
  );
  // One reader for the page's lifetime: its sequence orders every invitations read (first load,
  // Retry, the page controller's reloads and reconciles, the reload after the invite dialog
  // closes, a change of orgId) and is bumped when a send or create DTO is applied, so an older answer that lands last is
  // discarded, and so is any answer for an org the page has left (`createInvitationsReader`).
  const [invitationsReader] = useState(() =>
    createInvitationsReader({
      list: (id) => adminApi.listInvitations(id),
      currentOrgId: () => currentOrgIdRef.current,
      onLoaded: (list, readOrgId) => {
        publishInvitations(readOrgId, list);
        setInvitationsAvailability('supported');
        setInvitationsError(null);
      },
      onFailed: (err) => {
        const availability = resolveInvitationsAvailability(err);
        setInvitationsAvailability(availability);
        if (availability === 'error') {
          console.error('Failed to fetch invitations:', err);
          setInvitationsError('Failed to load invitations');
        }
      },
    }),
  );

  // Feedback tab state. Paging is continuation-token based (Cosmos cursor, not
  // skip/take) and appends: "Load more" fetches the next page and merges it into
  // the accumulated `feedback` list, de-duplicated by row id.
  const [feedback, setFeedback] = useState<OrgFeedbackItem[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<string>('All');
  const [feedbackNextToken, setFeedbackNextToken] = useState<string | undefined>(undefined);

  // Settings tab state
  const [editForm, setEditForm] = useState<UpdateOrganizationRequest>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Suspend dialog state
  const [showSuspend, setShowSuspend] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const fetchOrg = useCallback(async () => {
    if (!orgId) return;

    setLoading(true);
    // Clear the page-level error and every per-section error at the start of a (re)load.
    setError(null);
    setUsersError(null);
    setSubscriptionsError(null);
    setEntitlementsError(null);
    // Feedback loads/resets in its own effect (keyed on orgId + status filter), not here —
    // so a filter change doesn't force a whole-page reload of every section.

    // Org identity is genuinely required to render the page — load it alone first and bail if it fails.
    let orgData: AdminOrganization;
    try {
      orgData = await adminApi.getOrganization(orgId);
    } catch (err) {
      console.error('Failed to fetch org details:', err);
      setError('Failed to load organization details');
      setLoading(false);
      return;
    }

    setOrg(orgData);
    setEditForm({
      displayName: orgData.displayName || '',
      billingEmail: orgData.billingEmail || '',
      monthlySpendLimit: orgData.monthlySpendLimit,
      allowedDomains: orgData.allowedDomains,
      stripeCustomerId: orgData.stripeCustomerId || '',
      settings: {
        enableVoiceCalls: orgData.settings.enableVoiceCalls,
        enableSMS: orgData.settings.enableSMS,
        enableTeamsIntegration: orgData.settings.enableTeamsIntegration,
        requireApprovalForHighRiskActions: orgData.settings.requireApprovalForHighRiskActions,
        defaultTimeZone: orgData.settings.defaultTimeZone || undefined,
      },
    });

    // The remaining sections are non-critical: any one failing (e.g. an entitlements 403,
    // or the feedback endpoint being unavailable) must NOT blank the page. Load them
    // independently and scope each failure to its own section.
    const [usersResult, subsResult, entitlementResult] = await Promise.allSettled([
      usersReader.read(orgId, 0),
      adminApi.getOrganizationSubscriptions(orgId),
      adminApi.getEntitlements(orgId),
    ]);

    // The reader applies the rows itself (and drops a read that a later one superseded).
    if (usersResult.status === 'fulfilled' && usersResult.value === 'failed') {
      setUsersError('Failed to load users');
    }

    if (subsResult.status === 'fulfilled') {
      setSubscriptions(subsResult.value.subscriptions);
    } else {
      console.error('Failed to fetch subscriptions:', subsResult.reason);
      setSubscriptionsError('Failed to load subscriptions');
    }

    if (entitlementResult.status === 'fulfilled') {
      setEntitlements(entitlementResult.value.entitlements || []);
    } else {
      console.error('Failed to fetch entitlements:', entitlementResult.reason);
      setEntitlementsError('Failed to load entitlements');
    }

    setLoading(false);
  }, [orgId]);

  const fetchUsageSummary = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (!orgId) return;

    const seq = ++usageSummarySeqRef.current;

    if (!isParsleeStaff) {
      setUsageSummary(null);
      setUsageSummaryHidden(true);
      setUsageSummaryError(null);
      setUsageSummaryLoading(false);
      setUsageSummaryRefreshing(false);
      return;
    }

    setUsageSummaryHidden(false);
    if (mode === 'refresh') {
      setUsageSummaryRefreshing(true);
    } else {
      setUsageSummaryLoading(true);
    }
    setUsageSummaryError(null);

    try {
      const data = await adminApi.getOrganizationUsageSummary(orgId);
      if (seq !== usageSummarySeqRef.current) return;
      setUsageSummary(data);
      setUsageSummaryHidden(false);
      setUsageSummaryLastRefreshed(new Date());
    } catch (err) {
      if (seq !== usageSummarySeqRef.current) return;
      console.error('Failed to fetch onboarding and usage:', err);
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const failureState = resolveUsageSummaryFailure(status, isParsleeStaff);
      setUsageSummaryHidden(failureState.hidden);
      setUsageSummaryError(failureState.error);
      if (failureState.clearSummary) {
        setUsageSummary(null);
      }
    } finally {
      if (seq === usageSummarySeqRef.current) {
        setUsageSummaryLoading(false);
        setUsageSummaryRefreshing(false);
      }
    }
  }, [orgId, isParsleeStaff]);

  const fetchUsers = useCallback(
    // A delete, reactivate or add-staff continuation that lands after a move to another org reads nothing.
    async (skip: number): Promise<UsersReadResult> =>
      !orgId ? 'failed' : orgId !== currentOrgIdRef.current ? 'superseded' : usersReader.read(orgId, skip),
    [orgId, usersReader],
  );

  // After the limit dialog saves or closes, "Limit to" stays locked until the list is re-read, so a reopen never
  // seeds from a stale row (a 409, or a reopen racing the refresh, would otherwise overwrite a newer limit). The
  // read goes through usersReader, whose latest applied read releases the lock (onLoaded).
  const refreshUsersAfterLimit = useCallback((target: ClientLimitTarget) => {
    if (!isCurrentLimitOp(target, currentOrgIdRef.current, limitOpRef.current)) return;
    setLimitRows('refreshing');
    void usersReader.read(target.orgId, usersPageRef.current * 50);
  }, [usersReader]);

  // Every invitations read goes through the ordered reader, the same sequence pattern as
  // usageSummarySeqRef: 'applied' once this answer is
  // in state, 'superseded' when a later read or an applied send DTO made it stale (discarded),
  // 'failed' when the latest read failed. The page controller's reconcile releases a row only on 'applied'.
  const fetchInvitations = useCallback(async (): Promise<InvitationsReadResult> => {
    if (!orgId) return 'failed';
    return invitationsReader.read(orgId);
  }, [orgId, invitationsReader]);

  // A DTO from a send, or from the invite dialog's create, for the page's current org is newer
  // than every read still in flight: supersede them, then apply it (replacing its row, or adding a
  // just-created one), so an older reload landing later cannot restore the old link or drop the
  // new row. A DTO for any other org (work started before the route changed) is dropped:
  // supersede(forOrgId) returns false.
  const applyInvitationUpdate = useCallback((forOrgId: string, updated: AdminInvitation) => {
    if (!invitationsReader.supersede(forOrgId)) return;
    const list = invitationsForOrg(invitationsRef.current, forOrgId);
    publishInvitations(
      forOrgId,
      list.some((inv) => inv.id === updated.id)
        ? list.map((inv) => (inv.id === updated.id ? updated : inv))
        : [updated, ...list],
    );
  }, [invitationsReader, publishInvitations]);

  // The invitation actions live for the page's current org, not for a tab mount: the
  // Users subtree mounts only while `activeTab === 'users'`, so a controller owned by the
  // panel would be disposed by Users → Overview and replaced by an empty one on the way back, with
  // the page still holding the same rows. One controller per orgId, passed to the panel and the
  // invite dialog as `actions`; every callback is told which org's controller asks, so late work
  // for an org the page has left is dropped by the reader and applyInvitationUpdate. Nothing
  // re-fetches on a tab switch, and nothing needs to: the rows and the holds both live here.
  const invitationActions = useInvitationActions(orgId, {
    // Awaited by the controller: the row stays held until a reload the reader applied lands
    // (false: superseded and discarded, so the controller reads again), with Retry if it fails.
    onReconcile: async (forOrgId) => {
      const result = await invitationsReader.read(forOrgId);
      if (result === 'failed') throw new Error('Failed to reload invitations');
      return result === 'applied';
    },
    // A successful send's DTO is authoritative: apply it to its row now, then reload in the background.
    onSent: (forOrgId, sent) => {
      applyInvitationUpdate(forOrgId, sent);
      void invitationsReader.read(forOrgId);
    },
    // No usable link once the row is terminal, or for an org the page has left.
    currentAcceptUrl: (forOrgId, invitationId) => {
      const current = pageInvitation(
        { currentOrgId: currentOrgIdRef.current, invitations: invitationsRef.current },
        forOrgId,
        invitationId,
      );
      return current ? usableAcceptUrl(current, new Date()) : null;
    },
    // The page's row, which the controller compares with the send's baseline; none for another org.
    currentInvitation: (forOrgId, invitationId) =>
      pageInvitation(
        { currentOrgId: currentOrgIdRef.current, invitations: invitationsRef.current },
        forOrgId,
        invitationId,
      ),
  });

  // 'All' selects every status → send no status param (server returns all kinds).
  const feedbackStatusParam = feedbackStatusFilter === 'All' ? undefined : feedbackStatusFilter;

  // Fetch one page. Status is passed through to the server so paging and filtering
  // agree — the continuation token belongs to the FILTERED result set. In 'append'
  // mode the fetched page is merged into the existing list and de-duplicated by row
  // id (the same feedback row can be re-returned across a continuation-token page
  // boundary); in 'replace' mode (initial load / filter change) it starts fresh.
  const fetchFeedbackPage = useCallback(async (
    token: string | undefined,
    status: string | undefined,
    mode: 'replace' | 'append' = 'replace'
  ) => {
    if (!orgId) return;

    setFeedbackLoading(true);
    try {
      const data = await adminApi.listOrgFeedback(orgId, { status, continuationToken: token, limit: 50 });
      setFeedback(prev => {
        if (mode === 'replace') return data.items;
        const seenIds = new Set(prev.map(row => row.id));
        const deduped = data.items.filter(row => !seenIds.has(row.id));
        return [...prev, ...deduped];
      });
      setFeedbackNextToken(data.continuationToken);
      setFeedbackError(null);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
      setFeedbackError('Failed to load feedback');
    } finally {
      setFeedbackLoading(false);
    }
  }, [orgId]);

  const handleLoadMoreFeedback = () => {
    if (!feedbackNextToken) return;
    void fetchFeedbackPage(feedbackNextToken, feedbackStatusParam, 'append');
  };

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  useEffect(() => {
    usageSummarySeqRef.current += 1;
    setUsageSummary(null);
    setUsageSummaryError(null);
    setUsageSummaryLastRefreshed(null);
    setUsageSummaryHidden(!isParsleeStaff);

    if (orgId && isParsleeStaff) {
      void fetchUsageSummary('initial');
    }
  }, [orgId, isParsleeStaff, fetchUsageSummary]);

  // On return from Stripe card collection, open the Billing tab and surface the outcome, then
  // strip the one-shot query params so a refresh doesn't re-trigger the banner. Runs once on mount.
  useEffect(() => {
    const cardReturn = resolveCardCollectionReturn(
      window.location.pathname,
      window.location.search
    );
    if (!cardReturn) return;

    setActiveTab('billing');
    setCardResult(cardReturn.cardResult);
    window.history.replaceState({}, '', cardReturn.cleanUrl);
  }, []);

  useEffect(() => {
    fetchUsers(usersPage * 50);
  }, [usersPage, fetchUsers]);

  // fetchInvitations changes identity only with orgId. A new org starts from an empty, unprobed
  // list, so the previous org's rows (and their links) are never shown or acted on under this org
  // while its first read is out; the previous org's controller was disposed by the hook's cleanup
  // on the same orgId change. The dialog-open state is per org too: a back/forward
  // navigation between org detail URLs keeps this page mounted, and a dialog left open would
  // otherwise reopen for the new org once it loaded, prefilled from the old one.
  useEffect(() => {
    publishInvitations(orgId, []);
    setInvitationsAvailability('unknown');
    setInvitationsError(null);
    setShowInvite(false);
    setShowAddStaff(false);
    setInvitePrefill(null);
    limitOpRef.current += 1;
    setLimitTarget(null);
    setLimitRows('fresh');
    void fetchInvitations();
  }, [orgId, fetchInvitations, publishInvitations]);

  // Feedback is server-filtered: on orgId change or a status-filter change, reset the
  // accumulated list and continuation token, then re-fetch the first page WITH the
  // selected status. This is the single owner of feedback loading (not fetchOrg), so
  // filtering never depends on which page happens to be loaded. Guarded on orgId so it
  // no-ops until ready.
  useEffect(() => {
    if (!orgId) return;
    setFeedback([]);
    setFeedbackNextToken(undefined);
    void fetchFeedbackPage(undefined, feedbackStatusParam, 'replace');
  }, [orgId, feedbackStatusParam, fetchFeedbackPage]);

  const handleSave = async () => {
    if (!orgId) return;

    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await adminApi.updateOrganization(orgId, editForm);
      setOrg(updated);
      setSaveMessage('Saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!orgId || !suspendReason) return;

    try {
      await adminApi.suspendOrganization(orgId, suspendReason);
      setShowSuspend(false);
      setSuspendReason('');
      fetchOrg();
    } catch (err) {
      console.error('Failed to suspend:', err);
    }
  };

  const handleReactivate = async () => {
    if (!orgId) return;

    try {
      await adminApi.reactivateOrganization(orgId);
      fetchOrg();
    } catch (err) {
      console.error('Failed to reactivate:', err);
    }
  };

  const handleEntitlementToggle = async (productId: string, currentlyEnabled: boolean) => {
    if (!orgId) return;

    setError(null);
    setEntitlementLoading(prev => ({ ...prev, [productId]: true }));
    try {
      if (currentlyEnabled) {
        await adminApi.disableProduct(orgId, productId);
      } else {
        await adminApi.enableProduct(orgId, productId);
      }
      const data = await adminApi.getEntitlements(orgId);
      setEntitlements(data.entitlements || []);
      setEntitlementsError(null);
    } catch (err: any) {
      console.error('Failed to toggle entitlement:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.title || err?.message || 'Unknown error';
      setError(`Failed to ${currentlyEnabled ? 'disable' : 'enable'} ${productId}: ${msg}`);
    } finally {
      setEntitlementLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!orgId) return;

    setDeletingUserId(userId);
    try {
      await adminApi.deleteUser(orgId, userId);
      await fetchUsers(usersPage * 50);
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleReactivateUser = async (userId: string) => {
    if (!orgId) return;

    try {
      await adminApi.reactivateUser(orgId, userId);
      await fetchUsers(usersPage * 50);
    } catch (err) {
      console.error('Failed to reactivate user:', err);
      setError('Failed to reactivate user');
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const cls: Record<string, string> = {
      Active: ON_EMERALD, Trial: ON_AMBER, Suspended: ON_RED, Deleted: NEUTRAL_GRAY,
      Subscribed: ON_EMERALD, Unsubscribed: NEUTRAL_GRAY, PendingFulfillmentStart: ON_AMBER,
    };
    return <Badge variant="outline" className={cls[status] || NEUTRAL_GRAY}>{status}</Badge>;
  };

  const getRoleBadge = (role: string) => {
    const cls: Record<string, string> = {
      Owner: ON_EMERALD, Admin: ON_AMBER, Member: NEUTRAL_GRAY, Guest: NEUTRAL_GRAY,
    };
    return <Badge variant="outline" className={cls[role] || NEUTRAL_GRAY}>{role}</Badge>;
  };

  // Same color tokens as getStatusBadge, own status set (feedback triage lifecycle
  // rather than org/user lifecycle) with plain-language copy per FEEDBACK_STATUS_LABELS.
  const getFeedbackStatusBadge = (status: string) => {
    const cls: Record<string, string> = {
      New: ON_AMBER, Confirmed: ON_AMBER, Triaging: ON_AMBER,
      PrCreated: ON_AMBER, RevisionRequested: ON_AMBER,
      Deployed: ON_EMERALD,
      NotRepro: NEUTRAL_GRAY, Duplicate: NEUTRAL_GRAY,
      NeedsHuman: ON_RED,
    };
    return (
      <Badge variant="outline" className={cls[status] || NEUTRAL_GRAY}>
        {FEEDBACK_STATUS_LABELS[status] || status}
      </Badge>
    );
  };

  const handleBack = () => {
    window.location.href = backDestination.href;
  };

  // Rendered only from the list read for the org the page shows. Until the org-change reset lands,
  // the previous org's availability and dialog-open flags are not this org's either.
  const invitations = invitationsForOrg(invitationsState, orgId);
  const invitationsCurrent = invitationsState.orgId === orgId;
  const invitationsShown =
    invitationsCurrent && (invitationsAvailability === 'supported' || invitationsAvailability === 'error');

  if (loading) {
    return (
      <div className="org-detail-page">
        <div className="admin-loading">
          <Spinner size={32} label="Loading organization..." />
        </div>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="org-detail-page">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft />
          {backDestination.label}
        </Button>
        <div className="admin-error">
          <AlertTriangle className="h-5 w-5" />
          <span>{error || 'Organization not found'}</span>
        </div>
      </div>
    );
  }

  // Sort users: Owner first, then Admin, then others
  const rolePriority: Record<string, number> = { Owner: 0, Admin: 1, Member: 2, Guest: 3 };
  const sortedUsers = [...users].sort((a, b) =>
    (rolePriority[a.role] ?? 9) - (rolePriority[b.role] ?? 9)
  );

  // User summary stats (for overview)
  const activeUsers = users.filter(u => u.status === 'Active');
  const pendingUsers = users.filter(u => u.status === 'PendingActivation');
  const suspendedUsers = users.filter(u => u.status === 'Suspended');
  const owners = activeUsers.filter(u => u.role === 'Owner');

  // Duplicate detection: users sharing the same email
  const emailCounts = new Map<string, number>();
  for (const u of users.filter(u => u.status !== 'Deleted')) {
    const email = u.email.toLowerCase();
    emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
  }
  const duplicateEmails = [...emailCounts.entries()].filter(([, count]) => count > 1);

  // Users tab filtering
  const statusFilteredUsers = sortedUsers.filter(u => {
    if (!showDeleted && u.status === 'Deleted') return false;
    if (userStatusFilter === 'All') return true;
    return u.status === userStatusFilter;
  });
  const filteredUsers = userSearch
    ? statusFilteredUsers.filter(u =>
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.name || '').toLowerCase().includes(userSearch.toLowerCase())
      )
    : statusFilteredUsers;

  return (
    <div className="org-detail-page">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={handleBack}
        style={{ marginBottom: '1rem' }}
      >
        <ArrowLeft />
        {backDestination.label}
      </Button>

      {/* Header Card */}
      <Card className="org-header-card">
        <div className="org-header-top">
          <div className="org-header-title">
            <h1>{org.displayName || org.name}</h1>
            <div className="org-header-meta">
              {getStatusBadge(org.status)}
              <OrgProvisioningBadges org={org} />
              <CopyableId value={org.id} />
            </div>
          </div>
          <div className="org-header-actions">
            {org.status === 'Active' && (
              <Button
                variant="ghost"
                onClick={() => setShowSuspend(true)}
              >
                <Pause />
                Suspend
              </Button>
            )}
            {org.status === 'Suspended' && (
              <Button onClick={handleReactivate}>
                <Play />
                Reactivate
              </Button>
            )}
            {org.stripeCustomerId && (
              <a
                className={buttonVariants({ variant: 'ghost' })}
                href={`https://dashboard.stripe.com/customers/${org.stripeCustomerId}`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink />
                Stripe
              </a>
            )}
          </div>
        </div>

        <div className="org-header-stats">
          <div className="stat-item">
            <span className="stat-label">AI Employees</span>
            <span className="stat-value">{org.subscribedEmployeeCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Current Spend</span>
            <span className="stat-value">{formatCurrency(org.currentMonthSpend)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Spend Limit</span>
            <span className="stat-value">
              {org.monthlySpendLimit > 0 ? formatCurrency(org.monthlySpendLimit) : 'Unlimited'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Created</span>
            <span className="stat-value">{formatDate(org.createdAt)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Users</span>
            <span className="stat-value">{activeUsers.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">AIE Seats</span>
            <span className="stat-value">{org.subscribedEmployeeCount}</span>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="org-tabs flex flex-wrap gap-2 pb-3">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Overview
        </TabButton>
        <TabButton active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
          Billing
        </TabButton>
        <TabButton
          active={activeTab === 'tokenUsage'}
          onClick={() => setActiveTab('tokenUsage')}
        >
          Token Usage
        </TabButton>
        <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          Users ({activeUsers.length})
        </TabButton>
        <TabButton active={activeTab === 'feedback'} onClick={() => setActiveTab('feedback')}>
          Feedback
        </TabButton>
        <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
          Settings
        </TabButton>
      </div>

      <div className="org-tab-content">
        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {!usageSummaryHidden && (
              <OnboardingUsageCard
                summary={usageSummary}
                loading={usageSummaryLoading}
                refreshing={usageSummaryRefreshing}
                error={usageSummaryError}
                lastRefreshedAt={usageSummaryLastRefreshed}
                onRefresh={() => void fetchUsageSummary('refresh')}
              />
            )}

            {/* Info Grid */}
            <Card className="info-section">
              <h3>Organization Info</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">M365 Tenant</span>
                  <span className="info-value">
                    <CopyableId value={org.m365TenantId} />
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Connection Type</span>
                  <span className="info-value">
                    {org.connectionType
                      ? `${CONNECTION_TYPE_LABELS[org.connectionType] || org.connectionType} (${org.connectionType})`
                      : '-'}
                  </span>
                </div>
                {org.suspendedAt && (
                  <>
                    <div className="info-row">
                      <span className="info-label">Suspended At</span>
                      <span className="info-value">{formatDateTime(org.suspendedAt)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Suspension Reason</span>
                      <span className="info-value">{org.suspensionReason || '-'}</span>
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Billing Summary (compact — details on Billing tab) */}
            <Card className="info-section">
              <div className="section-header-inline">
                <h3>Billing</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('billing')}
                >
                  View billing details
                </Button>
              </div>
              <span>
                {org.subscribedEmployeeCount} AIE seat{org.subscribedEmployeeCount !== 1 ? 's' : ''} active
                {' '}&middot; {formatCurrency(org.currentMonthSpend)} this month
                {org.stripeCustomerId && (
                  <span className="text-muted-foreground" style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                    &middot; Stripe linked
                  </span>
                )}
              </span>
            </Card>

            {/* Settings Badges */}
            <Card className="info-section">
              <h3>Feature Flags</h3>
              <div className="settings-badges">
                <Badge variant="outline" className={org.settings.enableTeamsIntegration ? ON_EMERALD : OFF_GRAY}>
                  Teams: {org.settings.enableTeamsIntegration ? 'On' : 'Off'}
                </Badge>
                <Badge variant="outline" className={org.settings.enableVoiceCalls ? ON_EMERALD : OFF_GRAY}>
                  Voice: {org.settings.enableVoiceCalls ? 'On' : 'Off'}
                </Badge>
                <Badge variant="outline" className={org.settings.enableSMS ? ON_EMERALD : OFF_GRAY}>
                  SMS: {org.settings.enableSMS ? 'On' : 'Off'}
                </Badge>
                <Badge variant="outline" className={org.settings.requireApprovalForHighRiskActions ? ON_AMBER : OFF_GRAY}>
                  Approval Required: {org.settings.requireApprovalForHighRiskActions ? 'On' : 'Off'}
                </Badge>
              </div>
            </Card>

            {/* Product Entitlements */}
            <Card className="info-section">
              <div className="section-header-inline">
                <h3>Product Entitlements</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('billing')}
                >
                  View billing details
                </Button>
              </div>
              <SectionError message={entitlementsError} />
              <div className="entitlement-grid">
                {Object.entries(PRODUCT_CATALOG).map(([productId, meta]) => {
                  const ent = entitlements.find(e => e.productId === productId);
                  const isEnabled = ent?.enabled ?? false;
                  const isLoading = entitlementLoading[productId] ?? false;
                  return (
                    <div key={productId} className={`entitlement-card ${isEnabled ? 'enabled' : 'disabled'}`}>
                      <div className="entitlement-header">
                        <span className="entitlement-name">{meta.name}</span>
                        <Switch
                          checked={isEnabled}
                          disabled={isLoading}
                          onCheckedChange={() => handleEntitlementToggle(productId, isEnabled)}
                        />
                      </div>
                      {isEnabled && ent ? (
                        <div className="entitlement-details">
                          <Badge variant="outline">{ent.tier}</Badge>
                          <span className="entitlement-audit">
                            Enabled by {ent.enabledBy || 'system'} on {formatDate(ent.enabledAt)}
                          </span>
                        </div>
                      ) : (
                        <span className="entitlement-not-provisioned">Not provisioned</span>
                      )}
                      {productId === 'salestrainer' && (
                        <span className="entitlement-note">
                          Switching this on grants the free tier only: capped practice calls, no subscription.
                          For a free period, create a $0 deal with a trial length on the Billing tab. An org
                          that has held Trainer before skips the trial and gets an open-ended $0 deal.
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Users Summary */}
            <Card className="info-section">
              <div className="section-header-inline">
                <h3>Users</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('users')}
                >
                  Manage users
                </Button>
              </div>
              <SectionError message={usersError} />
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Active</span>
                  <span className="info-value">{activeUsers.length} user{activeUsers.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Owner</span>
                  <span className="info-value">
                    {owners.length > 0
                      ? owners.map(o => o.name || o.email).join(', ')
                      : <span className="text-destructive">No owner assigned</span>}
                  </span>
                </div>
                {pendingUsers.length > 0 && (
                  <div className="info-row">
                    <span className="info-label">Pending Activation</span>
                    <span className="info-value" style={{ color: 'hsl(var(--warning))' }}>
                      {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} stuck
                    </span>
                  </div>
                )}
                {suspendedUsers.length > 0 && (
                  <div className="info-row">
                    <span className="info-label">Suspended</span>
                    <span className="info-value" style={{ color: 'hsl(var(--destructive))' }}>
                      {suspendedUsers.length} user{suspendedUsers.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                {duplicateEmails.length > 0 && (
                  <div className="info-row">
                    <span className="info-label">Duplicates</span>
                    <span className="info-value" style={{ color: 'hsl(var(--destructive))' }}>
                      {duplicateEmails.length} email{duplicateEmails.length !== 1 ? 's' : ''} with duplicates
                    </span>
                  </div>
                )}
              </div>
            </Card>

            {/* Subscriptions Summary */}
            <Card className="info-section">
              <div className="section-header-inline">
                <h3>Subscriptions</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('billing')}
                >
                  View billing details
                </Button>
              </div>
              <SectionError message={subscriptionsError} />
              <span>
                {org.subscribedEmployeeCount} AIE seat{org.subscribedEmployeeCount !== 1 ? 's' : ''} active
                {subscriptions.length > 0 && (
                  <span className="text-muted-foreground"> &middot; {subscriptions.length} marketplace subscription{subscriptions.length !== 1 ? 's' : ''}</span>
                )}
              </span>
            </Card>
          </div>
        )}

        {/* ==================== USERS TAB ==================== */}
        {activeTab === 'users' && (
          <div className="users-tab">
            <SectionError message={usersError} />
            <SectionError message={limitRowsMessage(limitRows)} />

            {/* Duplicate detection banner */}
            {duplicateEmails.length > 0 && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-warning">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span>
                  <strong>Duplicate users detected:</strong>{' '}
                  {duplicateEmails.map(([email, count]) => `${email} (${count}x)`).join(', ')}
                </span>
              </div>
            )}

            <div className="tab-toolbar">
              <div className="relative" style={{ maxWidth: '300px' }}>
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="PendingActivation">Pending</SelectItem>
                  <SelectItem value="Suspended">Suspended</SelectItem>
                  <SelectItem value="All">All statuses</SelectItem>
                </SelectContent>
              </Select>
              <label className="text-muted-foreground" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={e => setShowDeleted(e.target.checked)}
                />
                Show deleted
              </label>
              {invitationsShown && (
                <>
                  <Button variant="secondary" size="sm" onClick={() => setShowAddStaff(true)}>
                    <UserPlus />
                    Add ButtonUp staff
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => { setInvitePrefill(null); setShowInvite(true); }}
                  >
                    <Mail />
                    Invite customer
                  </Button>
                </>
              )}
              <span className="text-muted-foreground" style={{ marginLeft: 'auto' }}>
                {filteredUsers.length} of {userCount} users
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table org-detail-table">
                <thead>
                  <tr>
                    <th style={{ width: '19%' }}>Email</th>
                    <th style={{ width: '13%' }}>Name</th>
                    <th style={{ width: '10%' }}>Role</th>
                    <th style={{ width: '10%' }}>Status</th>
                    <th style={{ width: '12%' }}>Last Login</th>
                    <th style={{ width: '12%' }}>M365 UPN</th>
                    <th style={{ width: '14%' }}>Limit to</th>
                    <th style={{ width: '10%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className={
                      duplicateEmails.some(([email]) => email === user.email.toLowerCase())
                        ? 'bg-warning/10'
                        : undefined
                    }>
                      <td title={user.email}>{user.email}</td>
                      <td>{user.name || '-'}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.status)}</td>
                      <td>{formatDate(user.lastLoginAt)}</td>
                      <td title={user.m365UserPrincipalName || '-'}>
                        {user.m365UserPrincipalName || '-'}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-h-11"
                          onClick={() => {
                            const next = openLimitTarget(limitTarget, orgId, user, limitOpRef.current + 1);
                            if (!next || next === limitTarget) return;
                            limitOpRef.current = next.op;
                            setLimitTarget(next);
                          }}
                          disabled={limitTarget !== null || !canOpenLimit(user, limitRows)}
                          title={`Limit to: ${summarizeScope(user.productLineScope)}`}
                          aria-label={`Limit to: ${summarizeScope(user.productLineScope)}`}
                        >
                          {summarizeScope(user.productLineScope)}
                        </Button>
                      </td>
                      <td>
                        {user.status === 'Active' || user.status === 'PendingActivation' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={deletingUserId === user.id}
                            onClick={() => {
                              if (window.confirm(`Delete user ${user.email}?`)) {
                                handleDeleteUser(user.id);
                              }
                            }}
                            title="Delete user"
                          >
                            <Trash2 />
                          </Button>
                        ) : user.status === 'Suspended' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleReactivateUser(user.id)}
                            title="Reactivate user"
                          >
                            <Play />
                          </Button>
                        ) : user.status === 'Deleted' ? (
                          <span className="text-muted-foreground" style={{ fontSize: '0.8rem' }}>Deleted</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {userCount > 50 && (
              <div className="pagination">
                <Button
                  variant="ghost"
                  disabled={usersPage === 0}
                  onClick={() => setUsersPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {usersPage + 1} of {Math.ceil(userCount / 50)}
                </span>
                <Button
                  variant="ghost"
                  disabled={(usersPage + 1) * 50 >= userCount}
                  onClick={() => setUsersPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
            {invitationsShown && (
              <InvitationsPanel
                invitations={invitations}
                loadError={invitationsError}
                onRetryLoad={() => void fetchInvitations()}
                actions={invitationActions}
                onNewInvitation={(inv) => {
                  setInvitePrefill({ email: inv.email, role: inv.role });
                  setShowInvite(true);
                }}
              />
            )}
          </div>
        )}

        {/* ==================== FEEDBACK TAB ==================== */}
        {activeTab === 'feedback' && (
          <div className="feedback-tab">
            <SectionError message={feedbackError} />

            <div className="tab-toolbar">
              <Select value={feedbackStatusFilter} onValueChange={setFeedbackStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All statuses</SelectItem>
                  {Object.entries(FEEDBACK_STATUS_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground" style={{ marginLeft: 'auto' }}>
                {feedback.length} loaded
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table org-detail-table">
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Kind</th>
                    <th style={{ width: '35%' }}>Description</th>
                    <th style={{ width: '20%' }}>Reporter</th>
                    <th style={{ width: '15%' }}>Status</th>
                    <th style={{ width: '15%' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbackLoading && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
                        Loading feedback...
                      </td>
                    </tr>
                  )}
                  {!feedbackLoading && feedback.map(row => (
                    <tr key={row.id}>
                      <td>{getFeedbackKindLabel(row)}</td>
                      <td title={row.description || undefined}>{truncateExcerpt(row.description)}</td>
                      <td title={row.reporterEmail || undefined}>{row.reporterEmail || '-'}</td>
                      <td>{getFeedbackStatusBadge(row.status)}</td>
                      <td>{formatDate(row.createdAt)}</td>
                    </tr>
                  ))}
                  {!feedbackLoading && !feedbackError && feedback.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
                        No feedback found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Appending pagination (continuation-token cursor, not skip/take): "Load more"
                fetches the next page and appends de-duplicated rows to the list above. */}
            {feedbackNextToken && (
              <div className="pagination">
                <Button
                  variant="ghost"
                  disabled={feedbackLoading}
                  onClick={handleLoadMoreFeedback}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ==================== BILLING TAB ==================== */}
        {activeTab === 'billing' && (
          <OrgBillingTab
            orgId={orgId!}
            cardCollectionResult={cardResult}
            cardCollectionReturnPath={cardCollectionReturnPath}
          />
        )}

        {/* ==================== TOKEN USAGE TAB ==================== */}
        {activeTab === 'tokenUsage' && <TokenUsageTab orgId={orgId!} />}

        {/* ==================== SETTINGS TAB ==================== */}
        {activeTab === 'settings' && (
          <div className="settings-tab">
            <Card className="settings-form">
              <h3>Organization Settings</h3>

              <div className="settings-field">
                <label>Display Name</label>
                <Input
                  value={editForm.displayName || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, displayName: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="settings-field">
                <label>Billing Email</label>
                <Input
                  value={editForm.billingEmail || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, billingEmail: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="settings-field">
                <label>Stripe Customer ID</label>
                <Input
                  value={editForm.stripeCustomerId || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, stripeCustomerId: e.target.value }))}
                  placeholder="cus_xxxxxxxxxxxxxx"
                  style={{ width: '100%' }}
                />
                <span className="block text-muted-foreground" style={{ fontSize: '0.85rem' }}>
                  Links this org to a Stripe customer for invoice sync and webhook processing
                </span>
              </div>

              <div className="settings-field">
                <label>Monthly Spend Limit ($)</label>
                <Input
                  type="number"
                  value={String(editForm.monthlySpendLimit || 0)}
                  onChange={(e) =>
                    setEditForm(f => ({ ...f, monthlySpendLimit: parseFloat(e.target.value) || 0 }))
                  }
                  style={{ width: '200px' }}
                />
                <span className="block text-muted-foreground" style={{ fontSize: '0.85rem' }}>
                  Set to 0 for unlimited
                </span>
              </div>

              <div className="settings-field">
                <label>Allowed Domains</label>
                <Input
                  value={(editForm.allowedDomains || []).join(', ')}
                  onChange={(e) =>
                    setEditForm(f => ({
                      ...f,
                      allowedDomains: e.target.value.split(',').map(d => d.trim()).filter(Boolean),
                    }))
                  }
                  placeholder="domain.com, other.com"
                  style={{ width: '100%' }}
                />
              </div>

              <h3 style={{ marginTop: '1.5rem' }}>Feature Toggles</h3>

              <div className="settings-toggles">
                <div className="toggle-row">
                  <Switch
                    checked={editForm.settings?.enableTeamsIntegration ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm(f => ({
                        ...f,
                        settings: { ...f.settings, enableTeamsIntegration: checked },
                      }))
                    }
                  />
                  <span>Teams Integration</span>
                </div>
                <div className="toggle-row">
                  <Switch
                    checked={editForm.settings?.enableVoiceCalls ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm(f => ({
                        ...f,
                        settings: { ...f.settings, enableVoiceCalls: checked },
                      }))
                    }
                  />
                  <span>Voice Calls</span>
                </div>
                <div className="toggle-row">
                  <Switch
                    checked={editForm.settings?.enableSMS ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm(f => ({
                        ...f,
                        settings: { ...f.settings, enableSMS: checked },
                      }))
                    }
                  />
                  <span>SMS</span>
                </div>
                <div className="toggle-row">
                  <Switch
                    checked={editForm.settings?.requireApprovalForHighRiskActions ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm(f => ({
                        ...f,
                        settings: { ...f.settings, requireApprovalForHighRiskActions: checked },
                      }))
                    }
                  />
                  <span>Require Approval for High-Risk Actions</span>
                </div>
              </div>

              <div className="settings-actions">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {saveMessage && (
                  <span className={saveMessage.includes('Failed') ? 'text-destructive' : 'text-success'}>
                    {saveMessage}
                  </span>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {showAddStaff && orgId && invitationsCurrent && (
        <AddStaffMemberDialog
          orgId={orgId}
          existingMemberAccountIds={users.map((u) => u.id)}
          onAdded={() => {
            setShowAddStaff(false);
            void fetchUsers(usersPage * 50);
          }}
          onClose={() => setShowAddStaff(false)}
        />
      )}

      {limitTarget && limitTargetUser(limitTarget, orgId) && (
        <ClientLimitDialog
          orgId={limitTarget.orgId}
          user={limitTarget.user}
          onSaved={(saved) => {
            if (!isCurrentLimitOp(limitTarget, currentOrgIdRef.current, limitOpRef.current)) return;
            setUsers((prev) => replaceUser(prev, saved));
            setLimitTarget(null);
            refreshUsersAfterLimit(limitTarget);
          }}
          onClose={() => {
            if (!isCurrentLimitOp(limitTarget, currentOrgIdRef.current, limitOpRef.current)) return;
            setLimitTarget(null);
            refreshUsersAfterLimit(limitTarget);
          }}
        />
      )}

      {showInvite && orgId && invitationsCurrent && (
        <InviteCustomerDialog
          orgId={orgId}
          initialEmail={invitePrefill?.email}
          initialRole={invitePrefill ? normalizeInvitationRole(invitePrefill.role) : undefined}
          invitations={invitations}
          actions={invitationActions}
          onInvitationCreated={applyInvitationUpdate}
          onInvited={() => {
            setShowInvite(false);
            void fetchInvitations();
          }}
          onClose={() => setShowInvite(false)}
        />
      )}

      {/* Suspend Confirmation */}
      {showSuspend && (
        <div className="suspend-overlay">
          <Card className="suspend-card">
            <h3>Suspend Organization</h3>
            <span className="block">
              All users will lose access and AIEs will be disabled.
            </span>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension..."
              rows={3}
              style={{ width: '100%', marginTop: '0.75rem' }}
            />
            <div className="suspend-actions">
              <Button variant="secondary" onClick={() => setShowSuspend(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleSuspend}
                disabled={!suspendReason}
              >
                Suspend
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default OrgDetailPage;
